<template>
  <v-container fluid class="dashboard-container pa-0 pb-12">
    <!-- Header: Identity & Daily Pulse -->
    <v-sheet color="white" class="pa-6 pa-md-8 mb-6 border-bottom shadow-xs">
      <v-row align="center" justify="space-between">
        <v-col cols="12" lg="6">
          <div class="d-flex align-center">
            <v-avatar size="52" color="primary lighten-5" class="mr-4 rounded-xl">
              <v-icon color="primary">fas fa-chart-line</v-icon>
            </v-avatar>
            <div>
              <div class="d-flex align-center flex-wrap" style="gap: 8px">
                <h1 class="text-h5 font-weight-black primary--text tracking-tight mb-0">
                  Dashboard Principal
                </h1>
                <v-chip v-if="asOf" x-small label color="blue-grey lighten-5" class="blue-grey--text text--darken-3 font-weight-medium">
                  <v-icon left x-small>fas fa-clock</v-icon>
                  Actualizado {{ formattedAsOf }}
                </v-chip>
              </div>
              <p class="body-2 grey--text text--darken-1 font-weight-medium mb-0 mt-1">
                Supervisión general de operaciones, ventas y compras · Período:
                <span class="font-weight-bold grey--text text--darken-3">{{ periodLabel }}</span>
              </p>
            </div>
          </div>
        </v-col>

        <!-- Daily Pulse Ticker & Actions -->
        <v-col cols="12" lg="6" class="d-flex align-center justify-lg-end flex-wrap" style="gap: 12px">
          <!-- Daily Pulse Card -->
          <div class="daily-pulse-card d-flex align-center px-4 py-2 rounded-xl">
            <div class="pulse-indicator mr-3"></div>
            <div>
              <div class="caption font-weight-bold text-uppercase grey--text text--darken-1">Pulso Hoy</div>
              <div class="d-flex align-baseline" style="gap: 6px">
                <span class="font-weight-black text-body-1 grey--text text--darken-4">
                  {{ todayConfirmed | moneda }}
                </span>
                <span class="caption font-weight-bold" :class="deltaClass(todayVsYesterdayPct)">
                  {{ deltaLabel(todayVsYesterdayPct) }} vs ayer
                </span>
              </div>
            </div>
          </div>

          <v-btn
            outlined
            color="primary"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            :loading="downloadingPdf"
            @click="downloadExecutivePdf"
          >
            <v-icon left small color="error">fas fa-file-pdf</v-icon>
            Exportar PDF
          </v-btn>

          <v-btn
            color="primary"
            depressed
            class="rounded-xl px-5 font-weight-bold text-none shadow-xs"
            :loading="loading"
            @click="fetch(true)"
          >
            <v-icon left small>fas fa-sync-alt</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="px-6 px-md-8">
      <!-- Time Control & Quick Presets Bar -->
      <v-card flat outlined class="filter-card pa-4 mb-6 shadow-xs">
        <v-row align="center" dense>
          <v-col cols="12" md="7">
            <div class="d-flex align-center flex-wrap" style="gap: 6px">
              <span class="caption font-weight-bold grey--text text--darken-2 mr-2 text-uppercase">
                Rango rápido:
              </span>
              <v-btn
                v-for="preset in presets"
                :key="preset.id"
                x-small
                depressed
                :color="activePreset === preset.id ? 'primary' : 'grey lighten-4'"
                :class="activePreset === preset.id ? 'white--text font-weight-bold' : 'grey--text text--darken-3 font-weight-medium'"
                class="rounded-lg text-none px-3 py-2"
                @click="applyPreset(preset)"
              >
                {{ preset.label }}
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12" md="5" class="d-flex align-center justify-md-end flex-wrap" style="gap: 8px">
            <div class="d-flex align-center" style="gap: 8px">
              <v-text-field
                v-model="startDate"
                label="Desde"
                outlined
                dense
                hide-details
                type="date"
                class="compact-date-input"
                @change="onManualDateChange"
              />
              <span class="grey--text">—</span>
              <v-text-field
                v-model="endDate"
                label="Hasta"
                outlined
                dense
                hide-details
                type="date"
                class="compact-date-input"
                @change="onManualDateChange"
              />
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Error State with Retry Button -->
      <v-alert
        v-if="error"
        dense
        outlined
        type="error"
        class="mb-6 rounded-xl bg-white"
      >
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <span>{{ error }}</span>
          <v-btn small color="error" depressed class="rounded-lg text-none font-weight-bold" @click="fetch(true)">
            <v-icon left small>fas fa-redo</v-icon>
            Reintentar
          </v-btn>
        </div>
      </v-alert>

      <!-- Loading Skeletons -->
      <v-row v-if="loading && !asOf" dense class="mb-6">
        <v-col v-for="i in 4" :key="'sk-' + i" cols="12" sm="6" md="3">
          <v-skeleton-loader type="card" height="136" class="rounded-xl shadow-xs" />
        </v-col>
      </v-row>

      <template v-else>
        <!-- Row 1: Hero KPIs (Configured by parameters) -->
        <v-row dense class="mb-6">
          <!-- 1. Venta Confirmada (North Star) -->
          <v-col v-if="parameters.dashboard_card_confirmed" cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100 is-primary">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Venta Confirmada</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Monto neto de cotizaciones que ya cuentan con Orden de Trabajo (OT) generada.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value text-center primary--text text--darken-3">
                  {{ total_confirmed | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">Ventas cerradas</span>
                    <span class="caption font-weight-bold teal--text text--darken-2">Activo</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- 2. Total Cotizado -->
          <v-col v-if="parameters.dashboard_card_quoted" cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Total Cotizado</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Suma acumulada de todas las notas de venta cotizadas creadas en el período.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value text-center grey--text text--darken-4">
                  {{ total_quoted | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">Proyecciones activas</span>
                    <span class="caption font-weight-bold grey--text text--darken-2">Pipeline</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- 3. Producción Finalizada -->
          <v-col v-if="parameters.dashboard_card_confirmed_finished" cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Producción Finalizada</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Monto de órdenes de trabajo finalizadas en planta listas para despacho o entrega.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value text-center grey--text text--darken-4">
                  {{ total_confirmed_finished | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">Listo para entrega</span>
                    <span class="caption font-weight-bold grey--text text--darken-2">Planta</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- 4. Pendiente Facturar -->
          <v-col v-if="parameters.dashboard_card_confirmed_finished_not_invoiced" cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Pendiente Facturar</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Órdenes finalizadas que no cuentan con DTE emitido asociado.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value text-center orange--text text--darken-3">
                  {{ total_confirmed_finished_not_invoiced | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">Finalizado sin DTE</span>
                    <span class="caption font-weight-bold orange--text text--darken-3">Requiere DTE</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- 5. Facturado Neto -->
          <v-col v-if="parameters.dashboard_card_invoiced" cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Facturado Neto</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Monto neto total facturado mediante DTE en el período.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value text-center grey--text text--darken-4">
                  {{ total_invoiced | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">Ingresos declarados</span>
                    <span class="caption font-weight-bold grey--text text--darken-2">Tributario</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- 6. Recaudación Real -->
          <v-col v-if="parameters.dashboard_card_paid" cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Recaudación Real</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Total de pagos percibidos y registrados en caja o banco.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value text-center teal--text text--darken-3">
                  {{ total_payments | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">Flujo de caja</span>
                    <span class="caption font-weight-bold teal--text text--darken-2">Caja Real</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- 7. Compras Neto -->
          <v-col v-if="parameters.dashboard_card_purchase_subtotal" cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Compras Neto</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Subtotal neto de facturas de compras registradas en el período.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value text-center grey--text text--darken-4">
                  {{ total_purchase_subtotal | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">Costos operativos</span>
                    <span class="caption font-weight-bold grey--text text--darken-2">Insumos</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- 8. Compras Pagado -->
          <v-col v-if="parameters.dashboard_card_purchase_paid" cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Compras Pagado</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Egresos reales pagados a proveedores en el período.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value text-center grey--text text--darken-4">
                  {{ total_purchase_payments | moneda }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">Egresos liquidados</span>
                    <span class="caption font-weight-bold grey--text text--darken-2">Pagos</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Row 2: Evolución en el Tiempo (Line Chart) -->
        <v-card class="panel-card mb-6 shadow-xs" flat>
          <div class="legend-row">
            <div>
              <div class="panel-title">Evolución en el Tiempo</div>
              <div class="panel-sub">Comparativa diaria de cotizado vs. venta confirmada</div>
            </div>
            <div class="legend">
              <span><i class="swatch quoted"></i> Cotizado</span>
              <span><i class="swatch sold"></i> Vendido</span>
            </div>
          </div>
          <div v-if="!hasTimelineData" class="empty-mini py-12 text-center">
            <v-icon color="grey lighten-1" class="mb-2" large>fas fa-chart-area</v-icon>
            <div>Sin movimientos comerciales registrados en este período</div>
          </div>
          <div v-else style="height: 300px">
            <line-chart ref="salesLineChart" :key="chartKey" :chart-data="lineData" :options="lineOptions" :height="300" />
          </div>
        </v-card>

        <!-- Relbase Integration Cards (Conditional) -->
        <template v-if="relbaseEnabled">
          <v-card class="panel-card mb-6 shadow-xs" flat>
            <div class="d-flex align-center justify-space-between mb-4">
              <div>
                <div class="panel-title">Sincronización Relbase DTE</div>
                <div class="panel-sub">Estado de emisión y facturación electrónica integrada</div>
              </div>
              <v-chip x-small color="teal lighten-5" class="teal--text text--darken-3 font-weight-bold">
                ENLACE DTE
              </v-chip>
            </div>

            <v-row v-if="loadingRelbase" dense>
              <v-col v-for="i in 4" :key="`relbase-skel-${i}`" cols="12" sm="6" md="3">
                <v-skeleton-loader type="card" height="100" class="rounded-xl"></v-skeleton-loader>
              </v-col>
            </v-row>

            <v-row v-else dense>
              <v-col cols="12" sm="6" md="3">
                <div class="pa-4 rounded-xl bg-slate-subtle border-light">
                  <div class="caption font-weight-bold grey--text text--darken-1 text-uppercase">Total Facturado Relbase</div>
                  <div class="text-h6 font-weight-black teal--text text--darken-3 mt-1">
                    {{ relbaseMetrics.totalFacturado | moneda }}
                  </div>
                  <div class="caption grey--text mt-1">DTEs electrónicos</div>
                </div>
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <div class="pa-4 rounded-xl bg-slate-subtle border-light">
                  <div class="caption font-weight-bold grey--text text--darken-1 text-uppercase">DTEs Recientes</div>
                  <div class="text-h6 font-weight-black text--primary mt-1">
                    {{ relbaseMetrics.dtesRecientes || 0 }}
                  </div>
                  <div class="caption grey--text mt-1">Emitidos en el período</div>
                </div>
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <div class="pa-4 rounded-xl bg-slate-subtle border-light">
                  <div class="caption font-weight-bold grey--text text--darken-1 text-uppercase">Pendientes Facturar</div>
                  <div class="text-h6 font-weight-black orange--text text--darken-3 mt-1">
                    {{ relbaseMetrics.pendientesFacturacion || 0 }} OTs
                  </div>
                  <div class="caption orange--text mt-1">Sin DTE asociado</div>
                </div>
              </v-col>

              <v-col cols="12" sm="6" md="3">
                <div class="pa-4 rounded-xl bg-slate-subtle border-light">
                  <div class="caption font-weight-bold grey--text text--darken-1 text-uppercase">Monto Pendiente</div>
                  <div class="text-h6 font-weight-black orange--text text--darken-3 mt-1">
                    {{ relbaseMetrics.montosPendientesFacturacion | moneda }}
                  </div>
                  <div class="caption grey--text mt-1">Por facturar en Relbase</div>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </template>

        <!-- Row 3: Commercial Rankings (Ejecutivo y Cliente) -->
        <v-row v-if="canViewCommercialDetails" dense class="mb-6">
          <!-- Columna 1: Por Ejecutivo Comercial -->
          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Por Ejecutivo Comercial</div>
                <span class="caption grey--text font-weight-bold">{{ topSellersList.length }} activos</span>
              </div>
              <div class="panel-sub mb-4">Venta confirmada y facturación en el período</div>
              
              <div v-if="!topSellersList.length" class="empty-mini py-6 text-center">Sin ventas confirmadas</div>
              
              <div
                v-for="row in topSellersList"
                :key="'s-' + row.id"
                class="rank-row"
              >
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">
                    {{ row.name }}
                  </div>
                  <div class="rank-val font-weight-bold">{{ row.confirmed | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">Facturado: {{ row.invoiced | moneda }}</div>
                  <span class="caption font-weight-bold grey--text text--darken-1">{{ row.pct }}% efectividad</span>
                </div>
                <div class="rank-track">
                  <div class="rank-fill" :style="{ width: rankWidth(row.confirmed, maxSeller) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Columna 2: Por Cliente -->
          <v-col cols="12" md="6">
            <v-card class="panel-card h-100 shadow-xs" flat>
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="panel-title">Por Cliente</div>
                <span class="caption grey--text font-weight-bold">{{ topCustomersList.length }} activos</span>
              </div>
              <div class="panel-sub mb-4">Venta confirmada y facturación en el período</div>
              
              <div v-if="!topCustomersList.length" class="empty-mini py-6 text-center">Sin clientes con venta</div>
              
              <div
                v-for="row in topCustomersList"
                :key="'c-' + row.id"
                class="rank-row"
              >
                <div class="d-flex align-center justify-space-between">
                  <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">
                    {{ row.name }}
                  </div>
                  <div class="rank-val font-weight-bold">{{ row.confirmed | moneda }}</div>
                </div>
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="rank-meta">Facturado: {{ row.invoiced | moneda }}</div>
                  <span class="caption font-weight-bold teal--text text--darken-2">{{ row.pct }}% facturado</span>
                </div>
                <div class="rank-track">
                  <div class="rank-fill is-teal" :style="{ width: rankWidth(row.confirmed, maxCustomer) }"></div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </div>
  </v-container>
</template>

<script>
import moment from 'moment'
import numeral from 'numeral'
import LineChart from '@/components/charts/LineChart.vue'
import { fetchDayGraph } from '@/api/salesNotes'
import { fetchRelbaseMetrics } from '@/api/relbase'
import { generateMainDashboardPdf } from '@/doc_generators/mainDashboardReport'

export default {
  name: 'Home',
  components: {
    LineChart
  },
  data: () => ({
    loading: false,
    loadingRelbase: false,
    downloadingPdf: false,
    error: null,
    asOf: null,

    // Control de tiempo y filtros rápidos (Idénticos al Dashboard de Ventas)
    activePreset: 'this_month',
    presets: [
      { id: 'today', label: 'Hoy' },
      { id: '7_days', label: '7 días' },
      { id: 'this_month', label: 'Mes actual' },
      { id: 'last_month', label: 'Mes anterior' },
      { id: 'qtd', label: 'Trimestre (QTD)' },
      { id: 'ytd', label: 'Año (YTD)' }
    ],
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),

    // Métricas operativas
    total_quoted: 0,
    total_confirmed: 0,
    total_confirmed_finished: 0,
    total_confirmed_finished_not_invoiced: 0,
    total_invoiced: 0,
    total_payments: 0,
    total_purchase_subtotal: 0,
    total_purchase_payments: 0,

    // Colecciones de rankings y series
    customers: [],
    sellers: [],
    quoted: [],
    confirmed: [],
    invoices: [],
    payments: [],
    topCustomersList: [],
    topSellersList: [],

    // Gráfico de evolución en el tiempo
    daily: [],
    hasTimelineData: false,
    lineData: { labels: [], datasets: [] },

    // Métricas Relbase
    relbaseMetrics: {
      totalFacturado: 0,
      dtesRecientes: 0,
      pendientesFacturacion: 0,
      montosPendientesFacturacion: 0
    }
  }),
  computed: {
    parameters() {
      return this.$store.getters.getCompany || {}
    },
    relbaseEnabled() {
      return !!this.parameters.relbase_integration_enabled
    },
    periodLabel() {
      if (!this.startDate && !this.endDate) return 'Todo el registro'
      const start = this.startDate ? moment(this.startDate).format('DD/MM/YYYY') : 'Inicio'
      const end = this.endDate ? moment(this.endDate).format('DD/MM/YYYY') : 'Hoy'
      return `${start} al ${end}`
    },
    formattedAsOf() {
      if (!this.asOf) return ''
      const tz = this.parameters.current_time_zone || 'America/Santiago'
      return `${moment(this.asOf).format('DD/MM/YYYY HH:mm')} (${tz})`
    },
    canViewCommercialDetails() {
      return this.$can('realAll', 'SalesNote') || this.$can('readAll', 'SalesNote') || this.$can('read', 'SalesNote')
    },
    todayConfirmed() {
      const todayStr = moment().format('YYYY-MM-DD')
      const item = (this.confirmed || []).find((c) => moment(c.date).format('YYYY-MM-DD') === todayStr)
      return item ? Number(item.total || 0) : 0
    },
    todayVsYesterdayPct() {
      const yesterdayStr = moment().subtract(1, 'day').format('YYYY-MM-DD')
      const itemYesterday = (this.confirmed || []).find((c) => moment(c.date).format('YYYY-MM-DD') === yesterdayStr)
      const yesterdayVal = itemYesterday ? Number(itemYesterday.total || 0) : 0
      if (yesterdayVal <= 0) return 0
      return Math.round(((this.todayConfirmed - yesterdayVal) / yesterdayVal) * 100)
    },
    maxSeller() {
      return this.maxOf(this.topSellersList, 'confirmed')
    },
    maxCustomer() {
      return this.maxOf(this.topCustomersList, 'confirmed')
    },
    chartKey() {
      return `${this.startDate || ''}-${this.endDate || ''}-${this.daily.length}`
    },
    lineOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#0f172a',
          titleFontColor: '#f8fafc',
          bodyFontColor: '#cbd5e1',
          borderColor: '#1e293b',
          borderWidth: 1,
          cornerRadius: 8,
          xPadding: 12,
          yPadding: 12,
          callbacks: {
            label: (item, data) => `${data.datasets[item.datasetIndex].label}: $${numeral(item.yLabel).format('0,0')}`
          }
        },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: { fontColor: '#64748b', fontSize: 11, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 }
          }],
          yAxes: [{
            gridLines: { color: '#f1f5f9', borderDash: [4, 4], drawBorder: false },
            ticks: {
              fontColor: '#64748b',
              fontSize: 11,
              callback: (value) => '$' + numeral(value).format('0,0a')
            }
          }]
        }
      }
    }
  },
  methods: {
    applyPreset(preset) {
      this.activePreset = preset.id
      const now = moment()

      switch (preset.id) {
        case 'today':
          this.startDate = now.clone().format('YYYY-MM-DD')
          this.endDate = now.clone().format('YYYY-MM-DD')
          break
        case '7_days':
          this.startDate = now.clone().subtract(6, 'days').format('YYYY-MM-DD')
          this.endDate = now.clone().format('YYYY-MM-DD')
          break
        case 'this_month':
          this.startDate = now.clone().startOf('month').format('YYYY-MM-DD')
          this.endDate = now.clone().format('YYYY-MM-DD')
          break
        case 'last_month':
          this.startDate = now.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
          this.endDate = now.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
          break
        case 'qtd':
          this.startDate = now.clone().startOf('quarter').format('YYYY-MM-DD')
          this.endDate = now.clone().format('YYYY-MM-DD')
          break
        case 'ytd':
          this.startDate = now.clone().startOf('year').format('YYYY-MM-DD')
          this.endDate = now.clone().format('YYYY-MM-DD')
          break
      }

      this.fetch(true)
    },
    onManualDateChange() {
      this.activePreset = 'custom'
      this.fetch(true)
    },
    deltaClass(value) {
      if (value == null) return ''
      if (value > 0) return 'is-up'
      if (value < 0) return 'is-down'
      return ''
    },
    deltaLabel(value) {
      if (value == null) return '—'
      const sign = value > 0 ? '+' : ''
      return `${sign}${value}%`
    },
    maxOf(rows, key) {
      let max = 1
      const field = key || 'confirmed'
      ;(rows || []).forEach((row) => {
        const value = Number(row[field] || 0)
        if (value > max) max = value
      })
      return max
    },
    rankWidth(val, max) {
      if (!max || max <= 0) return '0%'
      const pct = Math.min(100, Math.max(8, Math.round((Number(val || 0) / max) * 100)))
      return pct + '%'
    },
    async fetch(force = false) {
      this.loading = true
      this.error = null
      if (this.relbaseEnabled) this.loadingRelbase = true

      let filterSeller = ''
      if (!this.$can('realAll', 'SalesNote') && !this.$can('readAll', 'SalesNote')) {
        const user = this.$store.getters.getUser
        filterSeller = user ? user.id : ''
      }

      let created_before = ''
      let created_after = ''
      let emited_before = ''
      let emited_after = ''

      if (this.startDate) {
        created_after = moment(this.startDate).utc().format('YYYY-MM-DD HH:mm')
        emited_after = moment(this.startDate).format('YYYY-MM-DD')
      }
      if (this.endDate) {
        created_before = moment(this.endDate).utc().add(1439, 'minutes').format('YYYY-MM-DD HH:mm')
        emited_before = moment(this.endDate).format('YYYY-MM-DD')
      }

      const query = `created_after=${created_after}&created_before=${created_before}&emited_after=${emited_after}&emited_before=${emited_before}&customer__seller=${filterSeller}`

      const relbaseParams = {
        start_date: this.startDate || undefined,
        end_date: this.endDate || undefined
      }

      try {
        const [mainResult] = await Promise.allSettled([
          fetchDayGraph(query),
          this.relbaseEnabled ? this.loadRelbaseMetrics(relbaseParams) : Promise.resolve()
        ])

        if (mainResult.status === 'fulfilled') {
          const data = mainResult.value
          this.total_quoted = data.total_quoted || 0
          this.total_confirmed = data.total_confirmed || 0
          this.total_confirmed_finished = data.total_confirmed_finished || 0
          this.total_confirmed_finished_not_invoiced = data.total_confirmed_finished_not_invoiced || 0
          this.total_invoiced = data.total_invoiced || 0
          this.total_payments = data.total_payments || 0
          this.total_purchase_subtotal = data.total_purchase_subtotal || 0
          this.total_purchase_payments = data.total_purchase_payments || 0

          this.customers = data.customers || []
          this.sellers = data.sellers || []
          this.quoted = data.quoted || []
          this.confirmed = data.confirmed || []
          this.invoices = data.invoices || []
          this.payments = data.payments || []

          this.processRankings()
          this.buildChart()
          this.asOf = new Date()
        } else {
          this.error = 'No fue posible sincronizar las métricas operativas.'
          console.error('Error fetching dashboard data:', mainResult.reason)
        }
      } catch (err) {
        this.error = 'Error de conexión con el servidor.'
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    async loadRelbaseMetrics(params = {}) {
      if (!this.relbaseEnabled) return

      try {
        const data = await fetchRelbaseMetrics(params)
        this.relbaseMetrics = {
          totalFacturado: data.total_facturado || 0,
          dtesRecientes: data.dtes_recientes || 0,
          pendientesFacturacion: data.pendientes_facturacion || 0,
          montosPendientesFacturacion: data.monto_pendiente_facturacion || 0
        }
      } catch (e) {
        console.error('Error cargando métricas Relbase:', e)
      } finally {
        this.loadingRelbase = false
      }
    },
    processRankings() {
      // 1. Clientes
      if (this.customers && this.customers[0] && Array.isArray(this.customers[0])) {
        const invoicedList = (this.customers[1] && Array.isArray(this.customers[1])) ? this.customers[1] : []
        const customerDataSet = this.customers[0].map((customer) => {
          const invoicedCustomer = invoicedList.find((c) => c.customer__id === customer.customer__id)
          const confirmed = Number(customer.total || 0)
          const invoiced = Number(invoicedCustomer ? invoicedCustomer.total : 0)
          return {
            id: customer.customer__id,
            name: customer.name || 'Sin nombre',
            confirmed,
            invoiced,
            pct: confirmed > 0 ? Math.min(100, Math.round((invoiced / confirmed) * 100)) : 0
          }
        })
        this.topCustomersList = customerDataSet
          .sort((a, b) => b.confirmed - a.confirmed)
          .slice(0, 5)
      } else {
        this.topCustomersList = []
      }

      // 2. Vendedores
      if (this.sellers && this.sellers[0] && Array.isArray(this.sellers[0])) {
        const invoicedList = (this.sellers[1] && Array.isArray(this.sellers[1])) ? this.sellers[1] : []
        const sellerDataSet = this.sellers[0].map((seller) => {
          const invoicedSeller = invoicedList.find((s) => s.customer__seller__id === seller.customer__seller__id)
          const confirmed = Number(seller.total || 0)
          const invoiced = Number(invoicedSeller ? invoicedSeller.total : 0)
          return {
            id: seller.customer__seller__id,
            name: seller.full_name || 'Sin nombre',
            confirmed,
            invoiced,
            pct: confirmed > 0 ? Math.min(100, Math.round((invoiced / confirmed) * 100)) : 0
          }
        })
        this.topSellersList = sellerDataSet
          .sort((a, b) => b.confirmed - a.confirmed)
          .slice(0, 5)
      } else {
        this.topSellersList = []
      }
    },
    buildChart() {
      const start = moment(this.startDate)
      const end = moment(this.endDate)
      const days = []

      const diffDays = end.diff(start, 'days')
      if (diffDays >= 0 && diffDays <= 90) {
        const curr = start.clone()
        while (curr.isSameOrBefore(end, 'day')) {
          const dateStr = curr.format('YYYY-MM-DD')
          const label = curr.format('DD/MM')

          const qItem = (this.quoted || []).find((item) => moment(item.date).format('YYYY-MM-DD') === dateStr)
          const cItem = (this.confirmed || []).find((item) => moment(item.date).format('YYYY-MM-DD') === dateStr)

          days.push({
            date: dateStr,
            label,
            quoted: qItem ? Number(qItem.total || 0) : 0,
            confirmed: cItem ? Number(cItem.total || 0) : 0
          })
          curr.add(1, 'day')
        }
      } else {
        const dateMap = {}
        ;(this.quoted || []).forEach((item) => {
          const dStr = moment(item.date).format('YYYY-MM')
          if (!dateMap[dStr]) dateMap[dStr] = { date: dStr, label: moment(item.date).format('MMM YY'), quoted: 0, confirmed: 0 }
          dateMap[dStr].quoted += Number(item.total || 0)
        })
        ;(this.confirmed || []).forEach((item) => {
          const dStr = moment(item.date).format('YYYY-MM')
          if (!dateMap[dStr]) dateMap[dStr] = { date: dStr, label: moment(item.date).format('MMM YY'), quoted: 0, confirmed: 0 }
          dateMap[dStr].confirmed += Number(item.total || 0)
        })
        const sortedKeys = Object.keys(dateMap).sort()
        sortedKeys.forEach((k) => days.push(dateMap[k]))
      }

      this.daily = days
      this.hasTimelineData = days.some((d) => d.quoted > 0 || d.confirmed > 0)

      this.lineData = {
        labels: days.map((d) => d.label),
        datasets: [
          {
            label: 'Cotizado',
            data: days.map((d) => Number(d.quoted || 0)),
            borderColor: '#94a3b8',
            backgroundColor: 'transparent',
            borderWidth: 2,
            fill: false,
            lineTension: 0.35,
            pointRadius: 2.5,
            pointBackgroundColor: '#94a3b8'
          },
          {
            label: 'Vendido',
            data: days.map((d) => Number(d.confirmed || 0)),
            borderColor: '#0284c7',
            backgroundColor: 'rgba(2, 132, 199, 0.12)',
            borderWidth: 2.5,
            fill: true,
            lineTension: 0.35,
            pointRadius: 2.5,
            pointBackgroundColor: '#0284c7'
          }
        ]
      }
    },
    async downloadExecutivePdf() {
      this.downloadingPdf = true
      try {
        await generateMainDashboardPdf({
          company: this.parameters,
          parameters: this.parameters,
          startDate: this.startDate,
          endDate: this.endDate,
          serverTimezone: this.parameters.current_time_zone,
          metrics: {
            total_quoted: this.total_quoted,
            total_confirmed: this.total_confirmed,
            total_confirmed_finished: this.total_confirmed_finished,
            total_confirmed_finished_not_invoiced: this.total_confirmed_finished_not_invoiced,
            total_invoiced: this.total_invoiced,
            total_payments: this.total_payments,
            total_purchase_subtotal: this.total_purchase_subtotal,
            total_purchase_payments: this.total_purchase_payments
          },
          relbaseMetrics: this.relbaseMetrics,
          topCustomers: this.topCustomersList,
          topSellers: this.topSellersList
        })
      } catch (e) {
        console.error('Error generando Snapshot PDF del Dashboard:', e)
        if (this.$notify) {
          this.$notify({
            group: 'principal',
            type: 'error',
            title: 'Error de Exportación',
            text: 'No fue posible generar el snapshot PDF.'
          })
        }
      } finally {
        this.downloadingPdf = false
      }
    }
  },
  mounted() {
    this.fetch()
  }
}
</script>

<style scoped>
.dashboard-container {
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: 'Outfit', sans-serif !important;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-light {
  border: 1px solid #e2e8f0 !important;
}

.shadow-xs {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.rounded-xl {
  border-radius: 12px !important;
}

.tracking-tight {
  letter-spacing: -0.5px;
}

.bg-slate-subtle {
  background-color: #f8fafc;
}

/* Daily Pulse Ticker */
.daily-pulse-card {
  background: white;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.pulse-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  animation: pulse-green 2s infinite;
}

@keyframes pulse-green {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
}

/* Filters */
.filter-card {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
}

.compact-date-input {
  max-width: 155px;
}

.compact-date-input >>> .v-input__control {
  min-height: 36px !important;
}

.compact-date-input >>> .v-input__slot {
  min-height: 36px !important;
  font-size: 0.8rem !important;
  padding: 0 8px !important;
  border-radius: 8px !important;
}

/* Hero KPIs */
.hero-kpi-card {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.hero-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06) !important;
}

.hero-kpi-card.is-primary {
  border-color: #bae6fd !important;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
}

.kpi-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.kpi-value {
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.kpi-footer {
  border-top: 1px solid #f1f5f9;
}

/* Panels */
.panel-card {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  padding: 20px 22px;
}

.panel-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.panel-sub {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  margin-top: 2px;
}

.legend-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
}

.legend {
  display: flex;
  gap: 16px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.swatch {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.swatch.quoted { background: #94a3b8; }
.swatch.sold { background: #0284c7; }

/* Rankings & Tracks */
.rank-row {
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  transition: background 0.15s ease;
}

.rank-row:hover {
  background: #f8fafc;
}

.rank-name {
  font-size: 0.85rem;
  color: #0f172a;
}

.rank-val {
  font-size: 0.85rem;
  color: #0f172a;
}

.rank-meta {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.rank-track {
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
  margin-top: 6px;
}

.rank-fill {
  height: 100%;
  background: #0284c7;
  border-radius: 999px;
}

.rank-fill.is-teal {
  background: #0d9488;
}

.empty-mini {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
}

/* Deltas */
.is-up {
  color: #059669 !important;
}

.is-down {
  color: #e11d48 !important;
}
</style>
